import { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { 
  Gift, 
  BookOpen, 
  Package, 
  DollarSign, 
  Clock, 
  Check, 
  Truck, 
  Eye, 
  Trash2,
  Receipt,
  Calendar,
  Search,
  Filter,
  Mail,
  Send,
  FileText,
  Edit,
  Printer
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const DONATION_FORM_URL = process.env.REACT_APP_BACKEND_URL + '/donate';

export default function DonationManagement() {
  const [donations, setDonations] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All');
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedDonation, setSelectedDonation] = useState(null);
  const [updateStatus, setUpdateStatus] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  
  // Edit Mode State
  const [isEditMode, setIsEditMode] = useState(false);
  const [editForm, setEditForm] = useState({
    donor_name: '',
    donor_email: '',
    donor_phone: '',
    donor_address: '',
    donor_city: '',
    donor_state: '',
    donor_zip: '',
    organization_name: '',
    donation_type: '',
    book_quantity: '',
    bookshelf_quantity: '',
    condition: '',
    estimated_value: '',
    description: '',
    age_range: '',
    donation_reason: '',
    wants_tax_receipt: true,
    pickup_preferred: true,
    preferred_date: '',
    preferred_time: '',
    pickup_instructions: ''
  });
  
  // Send Form Link Dialog State
  const [showSendFormDialog, setShowSendFormDialog] = useState(false);
  const [sendFormEmail, setSendFormEmail] = useState('');
  const [sendFormName, setSendFormName] = useState('');
  const [sendFormSubject, setSendFormSubject] = useState('Complete Your Donation – The Bookshelf Project');
  const [sendFormBody, setSendFormBody] = useState('');
  const [sendingForm, setSendingForm] = useState(false);
  
  // Receipt Dialog State
  const [showReceiptDialog, setShowReceiptDialog] = useState(false);
  const [receiptHtml, setReceiptHtml] = useState('');
  const [loadingReceipt, setLoadingReceipt] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [donationsRes, metricsRes] = await Promise.all([
        fetch(`${API}/admin/donations`, { credentials: 'include' }),
        fetch(`${API}/admin/donations/metrics`, { credentials: 'include' })
      ]);

      if (donationsRes.ok) {
        const data = await donationsRes.json();
        setDonations(data);
      }
      if (metricsRes.ok) {
        const data = await metricsRes.json();
        setMetrics(data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load donations');
    } finally {
      setLoading(false);
    }
  };

  const openDetailDialog = (donation) => {
    setSelectedDonation(donation);
    setUpdateStatus(donation.status);
    setAdminNotes(donation.admin_notes || '');
    setScheduledDate(donation.scheduled_pickup_date || '');
    setIsEditMode(false);
    setEditForm({
      donor_name: donation.donor_name || '',
      donor_email: donation.donor_email || '',
      donor_phone: donation.donor_phone || '',
      donor_address: donation.donor_address || '',
      donor_city: donation.donor_city || '',
      donor_state: donation.donor_state || '',
      donor_zip: donation.donor_zip || '',
      organization_name: donation.organization_name || '',
      donation_type: donation.donation_type || '',
      book_quantity: donation.book_quantity || '',
      bookshelf_quantity: donation.bookshelf_quantity?.toString() || '',
      condition: donation.condition || '',
      estimated_value: donation.estimated_value?.toString() || '',
      description: donation.description || '',
      age_range: donation.age_range || '',
      donation_reason: donation.donation_reason || '',
      wants_tax_receipt: donation.wants_tax_receipt ?? true,
      pickup_preferred: donation.pickup_preferred ?? true,
      preferred_date: donation.preferred_date || '',
      preferred_time: donation.preferred_time || '',
      pickup_instructions: donation.pickup_instructions || ''
    });
    setShowDetailDialog(true);
  };

  const handleUpdateStatus = async () => {
    if (!selectedDonation) return;

    try {
      // Build update payload with all edited fields
      const updatePayload = {
        status: updateStatus,
        admin_notes: adminNotes,
        scheduled_pickup_date: scheduledDate,
        ...editForm,
        estimated_value: editForm.estimated_value ? parseFloat(editForm.estimated_value) : null,
        bookshelf_quantity: editForm.bookshelf_quantity ? parseInt(editForm.bookshelf_quantity) : null
      };

      console.log('=== DONATION UPDATE DEBUG ===');
      console.log('Donation ID:', selectedDonation.donation_id);
      console.log('Is Edit Mode:', isEditMode);
      console.log('Final Payload:', JSON.stringify(updatePayload, null, 2));

      const response = await fetch(`${API}/admin/donations/${selectedDonation.donation_id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updatePayload)
      });

      const responseData = await response.json();
      console.log('Response:', response.status, responseData);

      if (response.ok) {
        toast.success('Donation updated successfully');
        setShowDetailDialog(false);
        setIsEditMode(false);
        // Force a fresh fetch with cache-busting
        await fetchData();
      } else {
        console.error('Update failed:', responseData);
        toast.error(responseData.detail || 'Failed to update donation');
      }
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Failed to update donation: ' + error.message);
    }
  };

  const handleEditFormChange = (field, value) => {
    console.log(`Form field ${field} changed to:`, value);
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleViewReceipt = async (donation) => {
    setLoadingReceipt(true);
    try {
      const response = await fetch(`${API}/admin/donations/${donation.donation_id}/receipt`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setReceiptHtml(data.receipt_html);
        setSelectedDonation(donation);
        setShowReceiptDialog(true);
      } else {
        toast.error('Failed to load receipt');
      }
    } catch (error) {
      toast.error('Failed to load receipt');
    } finally {
      setLoadingReceipt(false);
    }
  };

  const printReceipt = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(receiptHtml);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const handleMarkReceiptSent = async (donationId) => {
    try {
      const response = await fetch(`${API}/admin/donations/${donationId}/tax-receipt`, {
        method: 'PUT',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Tax receipt marked as sent');
        fetchData();
      } else {
        toast.error('Failed to update');
      }
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async () => {
    if (!selectedDonation) return;

    try {
      const response = await fetch(`${API}/admin/donations/${selectedDonation.donation_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Donation deleted');
        setShowDeleteDialog(false);
        setSelectedDonation(null);
        fetchData();
      } else {
        toast.error('Failed to delete');
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const openSendFormDialog = () => {
    const defaultBody = `Hello {donor_name},

Thank you for your interest in donating books and/or bookshelves to The Bookshelf Project!

We're thrilled that you'd like to support our mission of building in-home libraries for children across the Washington, DC region.

To complete your donation, please fill out our online donation form:

${DONATION_FORM_URL}

The form will ask for:
• Your contact information
• Details about your donation (books, bookshelves, or both)
• Preferred pickup or drop-off arrangements
• Whether you'd like a tax-deductible receipt

Once submitted, our team will contact you within 2-3 business days to arrange the pickup or drop-off.

If you have any questions, please don't hesitate to reach out at info@thebookshelfproject.org or call (202) 993-4805.

Thank you for helping us cultivate a love of reading in children!

With gratitude,
The Bookshelf Project Team`;
    
    setSendFormBody(defaultBody);
    setSendFormEmail('');
    setSendFormName('');
    setSendFormSubject('Complete Your Donation – The Bookshelf Project');
    setShowSendFormDialog(true);
  };

  const handleSendFormLink = async () => {
    if (!sendFormEmail || !sendFormName) {
      toast.error('Please enter recipient name and email');
      return;
    }

    setSendingForm(true);
    try {
      // Replace {donor_name} placeholder with actual name
      const personalizedBody = sendFormBody.replace(/{donor_name}/g, sendFormName);
      
      const response = await fetch(`${API}/admin/donations/send-form-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          recipient_email: sendFormEmail,
          recipient_name: sendFormName,
          subject: sendFormSubject,
          body: personalizedBody
        })
      });

      if (response.ok) {
        toast.success(`Donation form link sent to ${sendFormEmail}`);
        setShowSendFormDialog(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send email');
      }
    } catch (error) {
      toast.error('Failed to send email');
    } finally {
      setSendingForm(false);
    }
  };

  const filteredDonations = donations.filter(d => {
    const matchesSearch = 
      d.donor_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.donor_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (d.organization_name && d.organization_name.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter === 'All' || d.status === statusFilter;
    const matchesType = typeFilter === 'All' || d.donation_type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  const getStatusBadge = (status) => {
    const styles = {
      'Pending': 'bg-yellow-100 text-yellow-800',
      'Scheduled': 'bg-blue-100 text-blue-800',
      'Completed': 'bg-green-100 text-green-800',
      'Cancelled': 'bg-red-100 text-red-800'
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{status}</Badge>;
  };

  const getTypeBadge = (type) => {
    const styles = {
      'Books': 'bg-purple-100 text-purple-800',
      'Bookshelves': 'bg-orange-100 text-orange-800',
      'Both': 'bg-teal-100 text-teal-800'
    };
    return <Badge className={styles[type] || 'bg-gray-100'}>{type}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#1a3a5c]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Donation Management
            </h1>
            <p className="text-slate-500">Track and manage community donations</p>
          </div>
          <Button 
            onClick={openSendFormDialog}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="send-form-link-btn"
          >
            <Send className="w-4 h-4 mr-2" />
            Send Donation Form Link
          </Button>
        </div>

        {/* Metrics Cards */}
        {metrics && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#1a3a5c]/10 rounded-full flex items-center justify-center">
                    <Gift className="w-5 h-5 text-[#1a3a5c]" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#1a3a5c]">{metrics.total}</p>
                    <p className="text-xs text-slate-500">Total Donations</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                    <Clock className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#1a3a5c]">{metrics.by_status.pending}</p>
                    <p className="text-xs text-slate-500">Pending</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <Truck className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#1a3a5c]">{metrics.by_status.scheduled}</p>
                    <p className="text-xs text-slate-500">Scheduled</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <Check className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#1a3a5c]">{metrics.by_status.completed}</p>
                    <p className="text-xs text-slate-500">Completed</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#1a3a5c]">
                      ${metrics.total_estimated_value?.toLocaleString() || 0}
                    </p>
                    <p className="text-xs text-slate-500">Est. Value</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                    <Receipt className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#1a3a5c]">{metrics.tax_receipts_pending}</p>
                    <p className="text-xs text-slate-500">Receipts Pending</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Donation Type Breakdown */}
        {metrics && (
          <div className="grid grid-cols-3 gap-4">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4 text-center">
                <BookOpen className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-[#1a3a5c]">{metrics.by_type.books}</p>
                <p className="text-sm text-slate-500">Book Donations</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4 text-center">
                <Package className="w-8 h-8 text-orange-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-[#1a3a5c]">{metrics.by_type.bookshelves}</p>
                <p className="text-sm text-slate-500">Bookshelf Donations</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4 text-center">
                <Gift className="w-8 h-8 text-teal-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-[#1a3a5c]">{metrics.by_type.both}</p>
                <p className="text-sm text-slate-500">Both Types</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters & Search */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search by name, email, or organization..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All Statuses</SelectItem>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Scheduled">Scheduled</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                  <SelectItem value="Cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All Types</SelectItem>
                  <SelectItem value="Books">Books</SelectItem>
                  <SelectItem value="Bookshelves">Bookshelves</SelectItem>
                  <SelectItem value="Both">Both</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Donations Table */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">All Donations ({filteredDonations.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-slate-500">Loading donations...</div>
            ) : filteredDonations.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Gift className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>No donations found</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Donor</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Value</TableHead>
                    <TableHead>Receipt</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDonations.map((donation) => (
                    <TableRow key={donation.donation_id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{donation.donor_name}</p>
                          <p className="text-xs text-slate-500">{donation.donor_email}</p>
                          {donation.organization_name && (
                            <p className="text-xs text-slate-400">{donation.organization_name}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{getTypeBadge(donation.donation_type)}</TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {donation.book_quantity && <p>📚 {donation.book_quantity} books</p>}
                          {donation.bookshelf_quantity && <p>📦 {donation.bookshelf_quantity} shelves</p>}
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(donation.status)}</TableCell>
                      <TableCell className="text-sm">
                        {formatDate(donation.created_at)}
                      </TableCell>
                      <TableCell>
                        {donation.estimated_value ? `$${donation.estimated_value.toLocaleString()}` : '-'}
                      </TableCell>
                      <TableCell>
                        {donation.wants_tax_receipt ? (
                          donation.tax_receipt_sent ? (
                            <Badge className="bg-green-100 text-green-800">Sent</Badge>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleMarkReceiptSent(donation.donation_id)}
                              className="text-orange-600 hover:text-orange-700"
                            >
                              <Receipt className="w-4 h-4 mr-1" />
                              Mark Sent
                            </Button>
                          )
                        ) : (
                          <span className="text-slate-400 text-sm">Not requested</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDetailDialog(donation)}
                            data-testid={`view-donation-${donation.donation_id}`}
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewReceipt(donation)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            title="View Receipt"
                            data-testid={`view-receipt-${donation.donation_id}`}
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => {
                              setSelectedDonation(donation);
                              setShowDeleteDialog(true);
                            }}
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Detail/Edit Dialog */}
        <Dialog open={showDetailDialog} onOpenChange={(open) => {
          setShowDetailDialog(open);
          if (!open) setIsEditMode(false);
        }}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle>{isEditMode ? 'Edit Donation' : 'Donation Details'}</DialogTitle>
                {!isEditMode && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsEditMode(true)}
                    className="ml-4"
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit Details
                  </Button>
                )}
              </div>
            </DialogHeader>
            {selectedDonation && (
              <div className="space-y-6">
                {/* Donor Info */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold text-[#1a3a5c] mb-3">Donor Information</h4>
                  {isEditMode ? (
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Donor Name *</Label>
                        <Input
                          value={editForm.donor_name}
                          onChange={(e) => handleEditFormChange('donor_name', e.target.value)}
                          data-testid="edit-donor-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Organization</Label>
                        <Input
                          value={editForm.organization_name}
                          onChange={(e) => handleEditFormChange('organization_name', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Email *</Label>
                        <Input
                          type="email"
                          value={editForm.donor_email}
                          onChange={(e) => handleEditFormChange('donor_email', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Phone *</Label>
                        <Input
                          value={editForm.donor_phone}
                          onChange={(e) => handleEditFormChange('donor_phone', e.target.value)}
                        />
                      </div>
                      <div className="md:col-span-2 space-y-2">
                        <Label>Street Address</Label>
                        <Input
                          value={editForm.donor_address}
                          onChange={(e) => handleEditFormChange('donor_address', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>City</Label>
                        <Input
                          value={editForm.donor_city}
                          onChange={(e) => handleEditFormChange('donor_city', e.target.value)}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-2">
                          <Label>State</Label>
                          <Input
                            value={editForm.donor_state}
                            onChange={(e) => handleEditFormChange('donor_state', e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>ZIP</Label>
                          <Input
                            value={editForm.donor_zip}
                            onChange={(e) => handleEditFormChange('donor_zip', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-slate-500">Donor Name</p>
                        <p className="font-medium">{selectedDonation.donor_name}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Organization</p>
                        <p className="font-medium">{selectedDonation.organization_name || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Email</p>
                        <p className="font-medium">{selectedDonation.donor_email}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Phone</p>
                        <p className="font-medium">{selectedDonation.donor_phone}</p>
                      </div>
                      <div className="md:col-span-2">
                        <p className="text-xs text-slate-500">Address</p>
                        <p className="font-medium">
                          {selectedDonation.donor_address}, {selectedDonation.donor_city}, {selectedDonation.donor_state} {selectedDonation.donor_zip}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Donation Details */}
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-semibold text-[#1a3a5c] mb-3">Donation Details</h4>
                  {isEditMode ? (
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Donation Type *</Label>
                        <Select value={editForm.donation_type} onValueChange={(v) => handleEditFormChange('donation_type', v)}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Books">Books</SelectItem>
                            <SelectItem value="Bookshelves">Bookshelves</SelectItem>
                            <SelectItem value="Both">Both</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Condition</Label>
                        <Select value={editForm.condition} onValueChange={(v) => handleEditFormChange('condition', v)}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="New">New</SelectItem>
                            <SelectItem value="Like New">Like New</SelectItem>
                            <SelectItem value="Good">Good</SelectItem>
                            <SelectItem value="Fair">Fair</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {(editForm.donation_type === 'Books' || editForm.donation_type === 'Both') && (
                        <div className="space-y-2">
                          <Label>Book Quantity</Label>
                          <Select value={editForm.book_quantity} onValueChange={(v) => handleEditFormChange('book_quantity', v)}>
                            <SelectTrigger data-testid="edit-book-quantity">
                              <SelectValue placeholder="Select quantity" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1-10">1-10 books</SelectItem>
                              <SelectItem value="11-25">11-25 books</SelectItem>
                              <SelectItem value="26-50">26-50 books</SelectItem>
                              <SelectItem value="51-100">51-100 books</SelectItem>
                              <SelectItem value="100+">100+ books</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      {(editForm.donation_type === 'Bookshelves' || editForm.donation_type === 'Both') && (
                        <div className="space-y-2">
                          <Label>Bookshelf Quantity</Label>
                          <Input
                            type="number"
                            min="1"
                            value={editForm.bookshelf_quantity}
                            onChange={(e) => handleEditFormChange('bookshelf_quantity', e.target.value)}
                            data-testid="edit-bookshelf-quantity"
                          />
                        </div>
                      )}
                      <div className="space-y-2">
                        <Label>Estimated Value ($)</Label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={editForm.estimated_value}
                            onChange={(e) => handleEditFormChange('estimated_value', e.target.value)}
                            placeholder="0.00"
                            className="pl-9"
                            data-testid="edit-estimated-value"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Age Range</Label>
                        <Select value={editForm.age_range || ''} onValueChange={(v) => handleEditFormChange('age_range', v)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select age range" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Baby/Toddler (0-3)">Baby/Toddler (0-3)</SelectItem>
                            <SelectItem value="Preschool (3-5)">Preschool (3-5)</SelectItem>
                            <SelectItem value="Early Reader (5-7)">Early Reader (5-7)</SelectItem>
                            <SelectItem value="Chapter Books (7-10)">Chapter Books (7-10)</SelectItem>
                            <SelectItem value="Middle Grade (10-13)">Middle Grade (10-13)</SelectItem>
                            <SelectItem value="Young Adult (13+)">Young Adult (13+)</SelectItem>
                            <SelectItem value="Mixed Ages">Mixed Ages</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="md:col-span-2 space-y-2">
                        <Label>Description</Label>
                        <Textarea
                          value={editForm.description}
                          onChange={(e) => handleEditFormChange('description', e.target.value)}
                          rows={2}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-slate-500">Donation Type</p>
                        <p className="font-medium">{selectedDonation.donation_type}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Condition</p>
                        <p className="font-medium">{selectedDonation.condition}</p>
                      </div>
                      {selectedDonation.book_quantity && (
                        <div>
                          <p className="text-xs text-slate-500">Book Quantity</p>
                          <p className="font-medium">{selectedDonation.book_quantity}</p>
                        </div>
                      )}
                      {selectedDonation.bookshelf_quantity && (
                        <div>
                          <p className="text-xs text-slate-500">Bookshelf Quantity</p>
                          <p className="font-medium">{selectedDonation.bookshelf_quantity}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-xs text-slate-500">Estimated Value</p>
                        <p className="font-medium">
                          {selectedDonation.estimated_value ? `$${selectedDonation.estimated_value}` : 'Not provided'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Age Range</p>
                        <p className="font-medium">{selectedDonation.age_range || '-'}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Genres */}
                {selectedDonation.genres && selectedDonation.genres.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-500 mb-2">Book Genres</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedDonation.genres.map((genre, i) => (
                        <Badge key={i} variant="outline">{genre}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Donation Photo */}
                {selectedDonation.image_url && (
                  <div>
                    <p className="text-xs text-slate-500 mb-2">Photo of Donation</p>
                    <div className="rounded-lg overflow-hidden border">
                      <img 
                        src={selectedDonation.image_url} 
                        alt="Donation" 
                        className="w-full max-h-64 object-contain bg-slate-50"
                      />
                    </div>
                  </div>
                )}

                {/* Description - only show in view mode since it's in edit form */}
                {!isEditMode && selectedDonation.description && (
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Description</p>
                    <p className="text-sm bg-slate-50 p-3 rounded">{selectedDonation.description}</p>
                  </div>
                )}

                {/* Reason */}
                {isEditMode ? (
                  <div className="space-y-2">
                    <Label>Why They're Donating</Label>
                    <Textarea
                      value={editForm.donation_reason}
                      onChange={(e) => handleEditFormChange('donation_reason', e.target.value)}
                      rows={2}
                    />
                  </div>
                ) : (
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Why They're Donating</p>
                    <p className="text-sm bg-slate-50 p-3 rounded">{selectedDonation.donation_reason}</p>
                  </div>
                )}

                {/* Pickup Info */}
                <div className="p-4 bg-yellow-50 rounded-lg">
                  <h4 className="font-semibold text-[#1a3a5c] mb-2">
                    {isEditMode ? 'Pickup/Drop-off Preference' : (selectedDonation.pickup_preferred ? '🚚 Pickup Requested' : '📍 Drop-off')}
                  </h4>
                  {isEditMode ? (
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="md:col-span-2 flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="pickup-preferred"
                          checked={editForm.pickup_preferred}
                          onChange={(e) => handleEditFormChange('pickup_preferred', e.target.checked)}
                          className="h-4 w-4"
                        />
                        <Label htmlFor="pickup-preferred">Pickup Requested</Label>
                      </div>
                      {editForm.pickup_preferred && (
                        <>
                          <div className="space-y-2">
                            <Label>Preferred Date</Label>
                            <Input
                              type="date"
                              value={editForm.preferred_date}
                              onChange={(e) => handleEditFormChange('preferred_date', e.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Preferred Time</Label>
                            <Select value={editForm.preferred_time || ''} onValueChange={(v) => handleEditFormChange('preferred_time', v)}>
                              <SelectTrigger>
                                <SelectValue placeholder="Select time" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Morning (8am-12pm)">Morning (8am-12pm)</SelectItem>
                                <SelectItem value="Afternoon (12pm-5pm)">Afternoon (12pm-5pm)</SelectItem>
                                <SelectItem value="Evening (5pm-8pm)">Evening (5pm-8pm)</SelectItem>
                                <SelectItem value="Flexible">Flexible</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="md:col-span-2 space-y-2">
                            <Label>Pickup Instructions</Label>
                            <Textarea
                              value={editForm.pickup_instructions}
                              onChange={(e) => handleEditFormChange('pickup_instructions', e.target.value)}
                              rows={2}
                              placeholder="Special instructions for pickup..."
                            />
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    selectedDonation.pickup_preferred && (
                      <div className="grid md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-xs text-slate-500">Preferred Date</p>
                          <p>{formatDate(selectedDonation.preferred_date) || 'Flexible'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Preferred Time</p>
                          <p>{selectedDonation.preferred_time || 'Flexible'}</p>
                        </div>
                        {selectedDonation.pickup_instructions && (
                          <div className="md:col-span-2">
                            <p className="text-xs text-slate-500">Instructions</p>
                            <p>{selectedDonation.pickup_instructions}</p>
                          </div>
                        )}
                      </div>
                    )
                  )}
                </div>

                {/* Update Status Section */}
                <div className="border-t pt-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-[#1a3a5c]">{isEditMode ? 'Status & Notes' : 'Update Status'}</h4>
                    {!isEditMode && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewReceipt(selectedDonation)}
                        className="text-blue-600 border-blue-200 hover:bg-blue-50"
                      >
                        <FileText className="w-4 h-4 mr-1" />
                        View Receipt
                      </Button>
                    )}
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Status</Label>
                      <Select value={updateStatus} onValueChange={setUpdateStatus}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Pending">Pending</SelectItem>
                          <SelectItem value="Scheduled">Scheduled</SelectItem>
                          <SelectItem value="Completed">Completed</SelectItem>
                          <SelectItem value="Cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {updateStatus === 'Scheduled' && (
                      <div className="space-y-2">
                        <Label>Scheduled Pickup Date</Label>
                        <Input
                          type="date"
                          value={scheduledDate}
                          onChange={(e) => setScheduledDate(e.target.value)}
                        />
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Admin Notes</Label>
                    <Textarea
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      placeholder="Add notes about this donation..."
                      rows={3}
                    />
                  </div>
                  <div className="flex gap-2">
                    {isEditMode && (
                      <Button 
                        variant="outline" 
                        onClick={() => setIsEditMode(false)} 
                        className="flex-1"
                      >
                        Cancel Edit
                      </Button>
                    )}
                    <Button 
                      onClick={handleUpdateStatus} 
                      className={`${isEditMode ? 'flex-1' : 'w-full'} bg-[#1a3a5c] hover:bg-[#1a3a5c]/90`}
                    >
                      {isEditMode ? 'Save All Changes' : 'Save Changes'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Donation</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-slate-600">
                Are you sure you want to delete this donation from{' '}
                <strong>{selectedDonation?.donor_name}</strong>?
              </p>
              <p className="text-sm text-red-600">
                This action cannot be undone.
              </p>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowDeleteDialog(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleDelete} className="flex-1 bg-red-600 hover:bg-red-700">
                  Delete Donation
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Send Donation Form Link Dialog */}
        <Dialog open={showSendFormDialog} onOpenChange={setShowSendFormDialog}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-purple-600" />
                Send Donation Form Link
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Send an email with a link to the donation form to someone who has contacted you about making a donation.
              </p>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="recipient-name">Recipient Name *</Label>
                  <Input
                    id="recipient-name"
                    value={sendFormName}
                    onChange={(e) => setSendFormName(e.target.value)}
                    placeholder="John Doe"
                    data-testid="send-form-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="recipient-email">Recipient Email *</Label>
                  <Input
                    id="recipient-email"
                    type="email"
                    value={sendFormEmail}
                    onChange={(e) => setSendFormEmail(e.target.value)}
                    placeholder="john@example.com"
                    data-testid="send-form-email"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email-subject">Subject</Label>
                <Input
                  id="email-subject"
                  value={sendFormSubject}
                  onChange={(e) => setSendFormSubject(e.target.value)}
                  data-testid="send-form-subject"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email-body">Email Body</Label>
                <Textarea
                  id="email-body"
                  value={sendFormBody}
                  onChange={(e) => setSendFormBody(e.target.value)}
                  rows={12}
                  className="font-mono text-sm"
                  data-testid="send-form-body"
                />
                <p className="text-xs text-slate-500">
                  Use {'{donor_name}'} as a placeholder - it will be replaced with the recipient's name.
                </p>
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button 
                  variant="outline" 
                  onClick={() => setShowSendFormDialog(false)} 
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleSendFormLink} 
                  disabled={sendingForm || !sendFormEmail || !sendFormName}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                  data-testid="confirm-send-form-btn"
                >
                  {sendingForm ? 'Sending...' : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Email
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Receipt Dialog */}
        <Dialog open={showReceiptDialog} onOpenChange={setShowReceiptDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Receipt className="w-5 h-5 text-[#1a3a5c]" />
                Donation Receipt
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {loadingReceipt ? (
                <div className="text-center py-8">
                  <div className="w-8 h-8 border-4 border-[#1a3a5c] border-t-transparent rounded-full animate-spin mx-auto"></div>
                  <p className="text-slate-500 mt-2">Loading receipt...</p>
                </div>
              ) : (
                <>
                  <div className="flex justify-end gap-2 mb-4">
                    <Button
                      variant="outline"
                      onClick={printReceipt}
                      className="flex items-center gap-2"
                    >
                      <Printer className="w-4 h-4" />
                      Print Receipt
                    </Button>
                  </div>
                  <div 
                    className="border rounded-lg overflow-hidden bg-white"
                    style={{ minHeight: '400px' }}
                  >
                    <iframe
                      srcDoc={receiptHtml}
                      title="Donation Receipt"
                      className="w-full h-[600px] border-0"
                      data-testid="receipt-iframe"
                    />
                  </div>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
